import { useState } from 'react';
import { Check, Circle, Clock, MoreHorizontal } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useDeleteReminder, useUpdateReminder } from '../../hooks/useReminders';
import { formatDueChip, formatEstimate } from '../../lib/reminderDates';
import { Popover } from '../tasks/linear/Popover';
import { ReminderForm, type ReminderFormValues } from './ReminderForm';
import type { Reminder } from '../../lib/remindersApi';

/**
 * One Google-Tasks-style row: circle-check to toggle done, title/description,
 * due chip (red once overdue), estimate chip, and a ⋯ menu (edit / delete).
 * Clicking Editează swaps the row for the shared inline form.
 */
export function ReminderRow({ reminder }: { reminder: Reminder }) {
  const { t, locale } = useI18n();
  const update = useUpdateReminder();
  const remove = useDeleteReminder();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="border-b border-border last:border-b-0">
        <ReminderForm
          reminder={reminder}
          busy={update.isPending}
          onCancel={() => setEditing(false)}
          onSave={(values: ReminderFormValues) =>
            update.mutate(
              { id: reminder.id, patch: values },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      </div>
    );
  }

  const overdue = !reminder.done && Date.parse(reminder.dueAt) < Date.now();

  return (
    <div className="group flex items-start gap-3 border-b border-border px-4 py-2.5 last:border-b-0 hover:bg-accent/50">
      <button
        type="button"
        disabled={update.isPending}
        onClick={() => update.mutate({ id: reminder.id, patch: { done: !reminder.done } })}
        title={reminder.done ? t('reminders.markOpen') : t('reminders.markDone')}
        aria-label={reminder.done ? t('reminders.markOpen') : t('reminders.markDone')}
        className="mt-0.5 shrink-0 text-muted-foreground hover:text-primary disabled:opacity-50"
      >
        {reminder.done ? <Check size={18} className="text-primary" /> : <Circle size={18} />}
      </button>

      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-[14px] ${
            reminder.done ? 'text-muted-foreground line-through' : 'text-foreground'
          }`}
        >
          {reminder.title}
        </p>
        {reminder.description && (
          <p className="truncate text-[12px] text-muted-foreground">{reminder.description}</p>
        )}
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 text-[12px] ${
              overdue ? 'font-medium text-red-600' : 'text-muted-foreground'
            }`}
          >
            <Clock size={12} />
            {formatDueChip(reminder.dueAt, locale, {
              today: t('reminders.today'),
              tomorrow: t('reminders.tomorrow'),
            })}
          </span>
          {reminder.estimatedMinutes != null && (
            <span className="rounded bg-accent px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {formatEstimate(reminder.estimatedMinutes)}
            </span>
          )}
        </div>
      </div>

      <Popover
        width={160}
        align="end"
        trigger={({ open, toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t('reminders.edit')}
            className={`shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground ${
              open ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100 touch:opacity-100'
            }`}
          >
            <MoreHorizontal size={16} />
          </button>
        )}
      >
        {(close) => (
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                close();
              }}
              className="block w-full px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-accent"
            >
              {t('reminders.edit')}
            </button>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => {
                remove.mutate(reminder.id);
                close();
              }}
              className="block w-full px-3 py-1.5 text-left text-[13px] text-red-600 hover:bg-accent disabled:opacity-50"
            >
              {t('reminders.delete')}
            </button>
          </div>
        )}
      </Popover>
    </div>
  );
}
